import React, { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { SelectField } from '@nicoknoll/forms';
import { classnames } from '@nicoknoll/utils';
import { useQuery } from '@tanstack/react-query';

import { eventPageListQuery, spaceDetailQuery } from '../data/queries.ts';
import {
    HOME_PATH,
    SITE_PAGE_DETAIL_PATH,
    STATIC_PAGE_LEGAL_NOTICE_PATH,
    STATIC_PAGE_PRIVACY_PATH,
    STATIC_PAGE_TERMS_PATH,
} from '../modules/site/paths.ts';
import { siteUrl, useEventId } from '../modules/site/utils.ts';

const Footer = ({
    className,
    isOnDarkBackground,
    ...props
}: Omit<React.ComponentPropsWithRef<'footer'>, 'children'> & {
    isOnDarkBackground?: boolean;
}) => {
    const { t, i18n } = useTranslation('components');

    const handleLanguageChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
        i18n.changeLanguage(e.target.value);

    const eventId = useEventId();
    const { data: pages } = useQuery({ ...eventPageListQuery({ eventId }), enabled: !!eventId });

    const { data: space } = useQuery(spaceDetailQuery());

    const terms = pages?.find((page) => page.role === 'TERMS');
    const privacy = pages?.find((page) => page.role === 'PRIVACY');
    const imprint = pages?.find((page) => page.role === 'IMPRINT');

    return (
        <footer
            className={classnames(
                'flex flex-none justify-center bg-base-50 py-12 px-6 border-t border-base-200 items-center',
                className
            )}
            {...props}
        >
            <div className="w-full max-w-[70rem] flex flex-col gap-6">
                <div className="flex gap-10 justify-between items-center max-sm:items-start max-sm:flex-col-reverse">
                    <section className={classnames('flex flex-col gap-4 items-start')}>
                        <Link
                            className="text-lg font-bold hover:text-theme-500 transition-colors select-none sm:group-data-[on-dark-background]:text-white"
                            to={HOME_PATH}
                        >
                            {space?.name}
                        </Link>

                        <section className="text-sm text-base-400 sm:group-data-[on-dark-background]:text-white/60">
                            &copy; {new Date().getFullYear()}. {t('footer.copyright')}
                        </section>
                    </section>

                    <section className="flex gap-6 text-sm items-center max-sm:flex-col">
                        {terms ? (
                            <Link
                                className="text-base-900 font-medium hover:text-theme-500 transition-colors select-none sm:group-data-[on-dark-background]:text-white"
                                to={siteUrl(SITE_PAGE_DETAIL_PATH, { eventId, pageId: terms.slug || terms.id })}
                            >
                                {t('footer.terms')}
                            </Link>
                        ) : (
                            <Link
                                className="text-base-900 font-medium hover:text-theme-500 transition-colors select-none sm:group-data-[on-dark-background]:text-white"
                                to={siteUrl(STATIC_PAGE_TERMS_PATH)}
                            >
                                {t('footer.terms')}
                            </Link>
                        )}

                        {privacy ? (
                            <Link
                                className="text-base-900 font-medium hover:text-theme-500 transition-colors select-none sm:group-data-[on-dark-background]:text-white"
                                to={siteUrl(SITE_PAGE_DETAIL_PATH, { eventId, pageId: privacy.slug || privacy.id })}
                            >
                                {t('footer.privacy')}
                            </Link>
                        ) : (
                            <Link
                                className="text-base-900 font-medium hover:text-theme-500 transition-colors select-none sm:group-data-[on-dark-background]:text-white"
                                to={siteUrl(STATIC_PAGE_PRIVACY_PATH)}
                            >
                                {t('footer.privacy')}
                            </Link>
                        )}

                        {imprint ? (
                            <Link
                                className="text-base-900 font-medium hover:text-theme-500 transition-colors select-none sm:group-data-[on-dark-background]:text-white"
                                to={siteUrl(SITE_PAGE_DETAIL_PATH, { eventId, pageId: imprint.slug || imprint.id })}
                            >
                                {t('footer.imprint')}
                            </Link>
                        ) : (
                            <Link
                                className="text-base-900 font-medium hover:text-theme-500 transition-colors select-none sm:group-data-[on-dark-background]:text-white"
                                to={siteUrl(STATIC_PAGE_LEGAL_NOTICE_PATH)}
                            >
                                {t('footer.imprint')}
                            </Link>
                        )}

                        <SelectField
                            className="w-full"
                            value={i18n.language}
                            hideSearch
                            hideClear
                            onChange={handleLanguageChange}
                            options={[
                                {
                                    value: 'en',
                                    // @ts-ignore
                                    label: t('common:languageCode.en'),
                                },
                                {
                                    value: 'de',
                                    // @ts-ignore
                                    label: t('common:languageCode.de'),
                                },
                            ]}
                        />
                    </section>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
